import Phaser from 'phaser';
import { GAME_WIDTH } from '../data/config';
import { DinoId, DINOS, DINO_ORDER } from '../data/dinos';
import { Sfx } from './Sfx';

export class Hud {
  private scene: Phaser.Scene;
  private hearts: Phaser.GameObjects.Image[] = [];
  private scoreText: Phaser.GameObjects.Text;
  private waveText: Phaser.GameObjects.Text;
  private dinoFace: Phaser.GameObjects.Sprite;
  private ammoIcon: Phaser.GameObjects.Image;
  private ammoText: Phaser.GameObjects.Text;
  private maxHp: number;

  constructor(scene: Phaser.Scene, dinoId: DinoId, mode: 'practice' | 'gauntlet' = 'practice') {
    this.scene = scene;
    const stats = DINOS[dinoId];
    this.maxHp = stats.maxHp;

    // Tiny dino face icon top-left. Falls back gracefully if the texture
    // hasn't been registered yet (HMR edge cases).
    const dinoTexKey = scene.textures.exists(`dino-${dinoId}`) ? `dino-${dinoId}` : 'star';
    this.dinoFace = scene.add
      .sprite(10, 10, dinoTexKey, 0)
      .setOrigin(0, 0)
      .setScale(1.4)
      .setScrollFactor(0)
      .setDepth(900);

    // Hearts row.
    for (let i = 0; i < this.maxHp; i++) {
      const h = scene.add
        .image(44 + i * 9, 14, 'heart')
        .setOrigin(0, 0.5)
        .setScrollFactor(0)
        .setDepth(900);
      this.hearts.push(h);
    }

    // Score top-right.
    this.scoreText = scene.add
      .text(GAME_WIDTH - 8, 8, '0', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(900);

    // Wave top-center.
    this.waveText = scene.add
      .text(GAME_WIDTH / 2, 8, 'WAVE 1', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(900);

    // Ammo: star icon + count, just below the hearts.
    this.ammoIcon = scene.add
      .image(44, 28, 'star')
      .setOrigin(0, 0.5)
      .setScale(1.4)
      .setScrollFactor(0)
      .setDepth(900);
    this.ammoText = scene.add
      .text(56, 24, '0', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(900);

    // God-mode indicator. Visible whenever window.__god is truthy. Updates
    // live via the G hotkey added in GameScene.
    const godText = scene.add
      .text(GAME_WIDTH - 8, 40, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ff3a6b',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(900);
    const refreshGod = () => {
      const on = !!(window as unknown as { __god?: boolean }).__god;
      godText.setText(on ? '[GOD]' : '');
    };
    refreshGod();
    scene.events.on('update', refreshGod);

    // Mute toggle, top-right just under the score.
    const muteLabel = () => (Sfx.isMuted() ? '[MUTE]' : '[SND]');
    const muteBtn = scene.add
      .text(GAME_WIDTH - 8, 24, muteLabel(), {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(900)
      .setInteractive({ useHandCursor: true });
    muteBtn.on('pointerdown', () => {
      Sfx.setMuted(!Sfx.isMuted());
      muteBtn.setText(muteLabel());
    });
    scene.input.keyboard?.on('keydown-M', () => {
      Sfx.setMuted(!Sfx.isMuted());
      muteBtn.setText(muteLabel());
    });
  }

  setHp(hp: number) {
    this.hearts.forEach((h, i) => {
      h.setAlpha(i < hp ? 1 : 0.18);
    });
  }

  setScore(score: number) {
    this.scoreText.setText(String(score).padStart(4, '0'));
  }

  setWave(wave: number) {
    this.waveText.setText(`WAVE ${wave}`);
  }

  setAmmo(ammo: number) {
    this.ammoText.setText(String(ammo));
    // Quick pulse on the icon to make pickups + shots feel responsive.
    this.scene.tweens.add({
      targets: this.ammoIcon,
      scale: { from: 2, to: 1.4 },
      duration: 140,
      ease: 'Back.easeOut',
    });
    // Dim if empty so the player notices.
    const empty = ammo <= 0;
    this.ammoIcon.setAlpha(empty ? 0.35 : 1);
    this.ammoText.setColor(empty ? '#666666' : '#ffe23a');
  }

  flashScore() {
    this.scene.tweens.add({
      targets: this.scoreText,
      scale: { from: 1.3, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
    });
  }
}
